<template>
  <div class="duration">
    <le-input-field
      ref="durationValue"
      :maxChars="3"
      :placeholder="`${$t('amd:alerts_configuration_duration_value_placeholder')}`"
      v-model="selectedDuration"
      class="duration__value"
      @input="emitChanges"
      @focus="onFocus"
      data-test="duration_input"
    />
    <le-select
      ref="durationUnit"
      theme="theme-light"
      class="duration__unit"
      type="dropdown"
      :items="units"
      :placeholder="`${$t('amd:alerts_configuration_duration_units_placeholder')}`"
      v-model="selectedUnit"
      @input="emitChanges"
      data-test="duration_select"
    />
  </div>
</template>

<script>
/* eslint-disable max-len */
import { each, get, isNull, values, map, isEmpty } from 'lodash';
import { leInputField, leSelect } from 'lp-vue-components';
import {
  DURATION_TIME_UNITS,
  DURATION_VALIDATION_TARGET,
} from '@config/Consts';

export default {
  name: 'duration',
  components: { leInputField, leSelect },
  props: {
    value: {
      type: Object,
      default: {},
    },
    validation: {
      type: Object,
      default: undefined,
      required: true,
    },
  },
  data() {
    return {
      selectedDuration: null,
      selectedUnit: null,
    };
  },
  watch: {
    value: {
      handler() {
        const selectedDuration = isNull(this.value) ? null : get(this.value, 'duration');
        const selectedUnit = isNull(this.value) ? null : this.formatForDropdown(get(this.value, 'unit'));
        this.selectedDuration = selectedDuration;
        this.selectedUnit = selectedUnit;
      },
      immediate: true,
    },
  },
  computed: {
    units() {
      return map(values(DURATION_TIME_UNITS), item => ({ id: item, value: item }));
    },
  },
  methods: {
    formatForDropdown(value) {
      return { id: value, value };
    },
    getInputRef(target) {
      let ref;
      switch (target) {
        case DURATION_VALIDATION_TARGET.VALUE:
          ref = this.$refs.durationValue;
          break;
        case DURATION_VALIDATION_TARGET.UNIT:
          ref = this.$refs.durationUnit;
          break;
        default:
          break;
      }
      return ref;
    },
    emitChanges() {
      this.$emit('input', {
        duration: this.selectedDuration,
        unit: isNull(this.selectedUnit) ? null : this.selectedUnit.id,
      });
    },
    setError(validationObj) {
      each(validationObj, (item) => {
        const ref = this.getInputRef(item.target);
        return ref && ref.setError(!item.isValid, item.message);
      });
    },
    clearError() {
      each([DURATION_VALIDATION_TARGET.DURATION_VALUE, DURATION_VALIDATION_TARGET.DURATION_UNIT], (item) => {
        const ref = this.getInputRef(item);
        return ref && ref.setError(false, '');
      });
    },
    setErrorClass(target) {
      const ref = this.getInputRef(target);
      return ref && ref.$el.classList.add('error-border');
    },
    removeErrorClass(target) {
      const ref = this.getInputRef(target);
      return ref && ref.$el.classList.remove('error-border');
    },
    validateNumeric(value) {
      if (isEmpty(value)) {
        this.setErrorClass(DURATION_VALIDATION_TARGET.VALUE);
        return { isValid: false, message: this.$t('amd:alerts_configuration_threshold_required_validation'), target: DURATION_VALIDATION_TARGET.VALUE };
      }
      const rangeFrom = this.validation.from;
      const rangeTo = this.validation.to;
      const isValidType = !isNaN(Number(value));
      const isInRange = isValidType && Number(value) >= rangeFrom && Number(value) <= rangeTo;
      if (!isValidType || !isInRange) {
        this.setErrorClass(DURATION_VALIDATION_TARGET.VALUE);
        return { isValid: false, message: this.$t('amd:alerts_configuration_threshold_range_validation', { from: rangeFrom, to: rangeTo }), target: DURATION_VALIDATION_TARGET.VALUE };
      }
      return { isValid: true, message: '', target: DURATION_VALIDATION_TARGET.VALUE };
    },
    validateUnit(unit) {
      if (isEmpty(unit && unit.value)) {
        this.setErrorClass(DURATION_VALIDATION_TARGET.UNIT);
        return { isValid: false, message: this.$t('amd:alerts_configuration_threshold_required_validation'), target: DURATION_VALIDATION_TARGET.UNIT };
      }
      return { isValid: true, message: '', target: DURATION_VALIDATION_TARGET.UNIT };
    },
    validate() {
      const valueValidation = this.validateNumeric(this.selectedDuration);
      const unitValidation = this.validateUnit(this.selectedUnit);
      return [valueValidation, unitValidation];
    },
    onFocus() {
      this.removeErrorClass(DURATION_VALIDATION_TARGET.VALUE);
    },
  },
};
</script>

<style lang="scss" scoped>
  .duration {
    display: flex;
    align-items: center;
    &__value {
      width: 50px;
      border: 1px solid transparent;
    }
    &__unit {
      margin-left: 5px;
      width: 100px;
      border: 1px solid transparent;
    }
    .error-border {
      @include themify {
        border: 1px solid getColor('alertValidationError', 'amd');
      }
    }
  }
</style>
